'use client'

/**
 * A collection's price chart, on the shared TradingView terminal.
 *
 * Drop-in for the legacy `./chart` component: same `{ slug, height, width }`
 * props, so call sites swap with a single import change.
 *
 * Two things it does that the old chart could not:
 *
 *   * **Denominations.** Satoshis, dollars, and market cap. The dollar and cap
 *     series are computed server-side against bitcoin's own candle, so a quiet
 *     collection's chart still moves — the holder's position did, because BTC
 *     moved under it. A flat line broken by occasional jumps was never an
 *     honest picture of a dollar position.
 *
 *   * **Social on the price axis.** Theses posted about this collection are
 *     hung on the time axis as marks, so "why did it move there" has an answer
 *     in the same view as the move. The feed is best-effort: a collection with
 *     no theses, or a social backend having a bad day, gets a clean chart
 *     rather than an error.
 */

import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import TvChart, { type ThesisMarker } from '@/components/tv-chart'
import { collectionSmartTag } from '@/app/alkanes/lib/collection-tag'
import { useBtcUsd, useMark } from '@/lib/quotes/hooks'
import type { Denom } from '@/components/tv-chart/symbol'
import useCollectionTwetchFeed from '@/app/alkanes/lib/use-collection-twetch-feed'

/** Longest mark tooltip before it is cut — marks are a pointer to the post,
 * not a place to read it. */
const MARK_TEXT_LIMIT = 280

/** Module constants, not inline literals.
 *
 * An array or object written inline in JSX is a NEW value on every render. Any
 * prop built that way is a reason for a child to re-run work keyed on it, and
 * for the chart that meant rebuilding the whole TradingView widget. Hoisting
 * them makes the identity stable for the life of the module. */
const COLLECTION_DENOMS: Denom[] = ['sats', 'usd', 'mcap']
const EMPTY_POSTS: never[] = []
const EMPTY_USERS: Record<string, never> = {}

const COLLECTION_DENOM_LABELS: Partial<Record<Denom, string>> = {
	sats: 'BTC',
	usd: 'USD',
	mcap: 'Mcap'
}

/**
 * Prop surface is the legacy `./chart` component's, so call sites swap with a
 * one-line import change and keep working. Several props describe chrome that
 * TradingView owns rather than us — they are accepted and ignored rather than
 * dropped from the type, so a call site that still passes them neither breaks
 * nor silently changes meaning.
 */
const TvCollectionChart: FC<{
	slug: string
	height?: number
	width?: number
	/** Show the social marks. Off for embeds too small to read them. */
	social?: boolean
	/**
	 * Legacy flag meaning "do not draw my own price header above the canvas —
	 * the parent renders one". TradingView draws its own legend, so this is
	 * accepted and ignored.
	 *
	 * It is deliberately NOT forwarded as `compact`: compact strips the entire
	 * toolbar — drawing tools, indicators, resolution tabs, range bar — and
	 * every call site passes `minimal`, so doing that shipped a chart with no
	 * controls at all on the surface users actually open.
	 */
	minimal?: boolean
	showPrice?: boolean
	showAxisLabels?: boolean
	showIdentity?: boolean
	headerActions?: React.ReactNode
	collection?: unknown
	/** Crosshair price, for a parent header that re-points to the cursor. */
	onHoverPrice?: (label: string | null) => void
	/** Visible window and its percent change, for the header's change stat. */
	onRangeChange?: (label: string, percentChange: number) => void
	className?: string
}> = ({ slug, height, width, social = true, onHoverPrice, onRangeChange, className }) => {
	// `active: false` keeps the request from firing at all when marks are off.
	// The collection's smart tag, the same identity the Social tab reads:
	// `ordinalswallet:slug/<slug>` (or a base64 form for slugs Twetch ids
	// cannot carry). The raw slug is not a tag and returned nobody's posts.
	const tag = useMemo(() => collectionSmartTag(slug), [slug])
	const feed = useCollectionTwetchFeed(social && tag !== null, slug)

	// A TAP token's circulating supply, from the same source the header
	// prices its market cap with, handed to the chart API only when it asks:
	// the API remembers the supply it is handed, so after the first viewer
	// of a token nobody waits on this. The chart mounts at once either way.
	const isTap = Boolean(slug?.toLowerCase().startsWith('tap-'))
	const supplyLookup = useRef<Promise<number | undefined> | null>(null)
	useEffect(() => {
		supplyLookup.current = null
	}, [slug])
	const resolveSupplyHint = useCallback((): Promise<number | undefined> => {
		if (!isTap) {
			return Promise.resolve(undefined)
		}
		if (!supplyLookup.current) {
			const ticker = slug.slice('tap-'.length)
			supplyLookup.current = fetch(`/api/tap-supply?ticker=${encodeURIComponent(ticker)}`, {
				signal: AbortSignal.timeout(6000)
			})
				.then((r) => (r.ok ? r.json() : null))
				.then((j) => (typeof j?.supply === 'number' && j.supply > 0 ? j.supply : undefined))
				.catch(() => undefined)
		}
		return supplyLookup.current
	}, [slug, isTap])

	// A price typical of the series, for sizing the axis before the first
	// bar arrives: the live mark in sats, through bitcoin for dollars. The
	// market cap needs a supply the page may not know; the feed reads a bar
	// for that case and remembers it.
	const mark = useMark(slug)
	const btcUsd = useBtcUsd()
	const typicalPrice = useCallback(
		(denom: Denom): number | undefined => {
			const sats = mark?.fair_sats
			if (!sats || !(sats > 0)) {
				return undefined
			}
			if (denom === 'sats') {
				return sats
			}
			if (denom === 'usd' && btcUsd && btcUsd > 0) {
				return (sats / 1e8) * btcUsd
			}
			return undefined
		},
		[mark?.fair_sats, btcUsd]
	)

	// Theses: the feed's posts with their authors, drawn as faces on the plot.
	const posts = feed?.posts ?? EMPTY_POSTS
	const users = feed?.users ?? EMPTY_USERS
	const theses = useMemo<ThesisMarker[]>(
		() => posts.map((post) => ({ post, user: users[String(post.userId)] })),
		[posts, users]
	)

	const style = useMemo(
		() => ({
			height: height ? `${height}px` : undefined,
			width: width ? `${width}px` : undefined
		}),
		[height, width]
	)

	return (
		// Fill the parent when no explicit size was given.
		//
		// Without `h-full` this wrapper had no height of its own, so the
		// `h-full` inside TvChart resolved against zero and the chart collapsed
		// to its `min-h` — a short chart floating above a tall empty column,
		// whatever height the container was given.
		<div style={style} className={classNames(height ? undefined : 'h-full', 'w-full', className)}>
			<TvChart
				kind="collection"
				id={slug}
				// Market cap by default: it is the number traders compare
				// collections by, and it is the one denomination that keeps
				// moving when the collection itself is quiet, because bitcoin
				// does. A collection with no known supply falls back on its
				// own — see `onDenomUnavailable` in TvChart.
				denom="mcap"
				denoms={COLLECTION_DENOMS}
				resolution="1D"
				denomLabels={COLLECTION_DENOM_LABELS}
				resolveSupplyHint={resolveSupplyHint}
				typicalPrice={typicalPrice}
				theses={social ? theses : undefined}
				onHoverPrice={onHoverPrice}
				onRangeChange={onRangeChange}
			/>
		</div>
	)
}

export default TvCollectionChart
